import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatCurrency, formatDate } from '@/utils/formatters';

import { settledFromTheFeed, type PendingUnlink } from './investorPayments';

/**
 * Confirms taking a payment off an investor. A bank transaction returns to
 * the reconciliation inbox; a hand-recorded entry does not.
 */
export function UnlinkPaymentDialog({
  pending,
  onCancel,
  onConfirm,
}: {
  pending: PendingUnlink | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const fromTheFeed = pending != null && settledFromTheFeed(pending.payment);

  return (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => !open && onCancel()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {fromTheFeed
              ? 'Unlink this bank transaction?'
              : 'Delete this entry?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {pending && describeUnlink(pending)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {fromTheFeed ? 'Unlink' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function describeUnlink({ payment, investorName }: PendingUnlink): string {
  const amount = formatCurrency(payment.amount, { currency: payment.currency });
  const when = formatDate(payment.value_date ?? payment.payment_date);
  if (settledFromTheFeed(payment)) {
    return (
      `The ${amount} bank transaction dated ${when} goes back to the reconciliation ` +
      `inbox, where it can settle something else. ${investorName} is credited for ` +
      `it no longer.`
    );
  }
  return (
    `Nothing else records the ${amount} received from ${investorName} on ${when} - ` +
    `there is no bank transaction behind it, so this entry goes for good.`
  );
}
